import { z } from "zod";
import { isValidWindow } from "@nexus/contracts";
import type {
  DashboardMode,
  CompetitorFilters,
  ExploreDateSelectionFields,
  MyBrandFilters,
} from "../types/dashboard.types";
import {
  CUSTOM_PRESET_ID,
  DEFAULT_PRESET_ID,
  isKnownPresetId,
  legacyTokenToPresetId,
  normalizeDateSelection,
  presetIdToLegacyToken,
} from "./explore-date-presets";
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_COMPETITOR_FILTERS,
} from "../types/dashboard.types";

export type ExploreSearchPersistOptions = {
  persistCustomWindow?: boolean;
};

const DATE_RANGE_VALUES = DATE_RANGE_OPTIONS.map((o) => o.value) as [
  (typeof DATE_RANGE_OPTIONS)[number]["value"],
  ...(typeof DATE_RANGE_OPTIONS)[number]["value"][],
];

const MODE_VALUES = ["my-brand", "competitors"] as const;

/** Sentinel for "every selectable account" — kept out of the URL as a full id CSV. */
export const ALL_ACCOUNTS = "all";

export const EXPLORE_ROUTES = new Set(["/dashboard", "/overview", "/drilldown"]);

export const exploreSearchSchema = z.object({
  mode: z.enum(MODE_VALUES).catch("competitors"),
  dateRange: z.enum(DATE_RANGE_VALUES).catch("90d"),
  /**
   * The active mode's date preset, mirroring how the single `dateRange` param
   * already means the active mode's token.
   *
   * An unrecognized id resolves to `undefined` and is thereafter treated as
   * absent, so a legacy `dateRange` in the same URL still governs. Degrading it
   * to the default preset here would discard a usable token.
   */
  presetId: z
    .string()
    .optional()
    .transform((value) => (value && isKnownPresetId(value) ? value : undefined))
    .catch(undefined),
  platforms: z.string().optional().catch(undefined),
  myIndustry: z.string().optional().catch(undefined),
  myBrand: z.string().optional().catch(undefined),
  competitorsIndustry: z.string().optional().catch(undefined),
  mainCompetitor: z.string().optional().catch(undefined),
  benchmark: z.string().optional().catch(undefined),
  accounts: z.string().optional().catch(undefined),
  customStart: z.string().optional().catch(undefined),
  customEnd: z.string().optional().catch(undefined),
});

export type ExploreSearch = z.infer<typeof exploreSearchSchema>;

export const DEFAULT_EXPLORE_SEARCH: ExploreSearch = {
  mode: "competitors",
  dateRange: "90d",
};

const CSV_STRING_FIELDS = [
  "platforms",
  "benchmark",
  "accounts",
  "myIndustry",
  "myBrand",
  "competitorsIndustry",
  "mainCompetitor",
] as const;

/** Router `parseSearchWith` coerces digit-only params to numbers — keep ids as strings. */
export function normalizeExploreSearchInput(
  search: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...search };
  for (const key of CSV_STRING_FIELDS) {
    if (out[key] != null) out[key] = String(out[key]);
  }
  return out;
}

export function parseExploreSearch(raw: Record<string, unknown>): ExploreSearch {
  return exploreSearchSchema.parse(normalizeExploreSearchInput(raw));
}

export function validateExploreSearch(search: Record<string, unknown>): ExploreSearch {
  return parseExploreSearch(search);
}

function splitCsv(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinCsv(values: string[]): string | undefined {
  if (values.length === 0) return undefined;
  return values.join(",");
}

/** Prune platform slugs to an authorized allow-list (empty csv → undefined). */
export function pickPlatforms(
  csv: string | undefined,
  allowedPlatformIds: string[],
): string | undefined {
  if (!csv || allowedPlatformIds.length === 0) return csv;
  const allowed = new Set(allowedPlatformIds);
  const picked = splitCsv(csv).filter((id) => allowed.has(id));
  if (picked.length === 0) return undefined;
  return picked.join(",");
}

function sortedCsv(value: string | undefined): string | undefined {
  const parts = splitCsv(value);
  if (parts.length === 0) return undefined;
  return [...parts].sort().join(",");
}

function canonicalizeExploreSearch(search: ExploreSearch): ExploreSearch {
  const accounts =
    search.accounts === ALL_ACCOUNTS ? undefined : sortedCsv(search.accounts);
  return {
    mode: search.mode,
    // Verbatim: this normalizes a URL for comparison and must not derive values.
    dateRange: search.dateRange,
    // Omitting presetId here would make a URL that disagrees with applied state
    // compare as already in sync, and the URL would stop tracking the selection.
    presetId: search.presetId,
    platforms: sortedCsv(search.platforms),
    myIndustry: search.myIndustry,
    myBrand: search.myBrand,
    competitorsIndustry: search.competitorsIndustry,
    mainCompetitor: search.mainCompetitor,
    benchmark: sortedCsv(search.benchmark),
    accounts,
    customStart: search.customStart,
    customEnd: search.customEnd,
  };
}

export function exploreSearchEquals(a: ExploreSearch, b: ExploreSearch): boolean {
  return (
    JSON.stringify(canonicalizeExploreSearch(a)) ===
    JSON.stringify(canonicalizeExploreSearch(b))
  );
}

export function exploreSearchToCompetitorFilters(
  search: ExploreSearch,
  productFallback: Pick<CompetitorFilters, "industryProducts">,
  options: ExploreSearchPersistOptions = {},
): CompetitorFilters {
  const platforms = splitCsv(search.platforms);
  return {
    myIndustry: search.myIndustry ?? null,
    myBrand: search.myBrand ?? null,
    industryProducts: productFallback.industryProducts,
    competitorsIndustry: search.competitorsIndustry ?? null,
    mainCompetitor: search.mainCompetitor ?? null,
    benchmark: splitCsv(search.benchmark),
    platforms: platforms.length > 0 ? platforms : [...DEFAULT_COMPETITOR_FILTERS.platforms],
    dateRange: dateRangeFromSearch(search),
    ...dateSelectionFromSearch(search, options.persistCustomWindow === true),
  };
}

/**
 * Date fields for a hydrated filter object.
 *
 * `presetId` is authoritative and the legacy token is the fallback, because the
 * explore routes carry no absolute date params — the preset is the only restore
 * path. Absolute dates stay empty so the consumer resolves the preset against
 * today rather than a date captured when the link was made.
 */
function dateSelectionFromSearch(
  search: ExploreSearch,
  persistCustomWindow: boolean,
): ExploreDateSelectionFields {
  if (persistCustomWindow && search.presetId === CUSTOM_PRESET_ID) {
    const startDate = search.customStart ?? "";
    const endDate = search.customEnd ?? "";
    if (isValidWindow({ startDate, endDate })) {
      return { startDate, endDate, activePresetId: CUSTOM_PRESET_ID };
    }
  }
  return normalizeDateSelection({
    startDate: "",
    endDate: "",
    activePresetId: search.presetId ?? legacyTokenToPresetId(search.dateRange),
  });
}

/**
 * The token a hydrated filter object should carry.
 *
 * Derived from `presetId` when that preset maps to one. Presets with no legacy
 * token fall through to the URL's own token, mirroring how `setDateSelection`
 * leaves it at its prior value on the write path.
 */
function dateRangeFromSearch(search: ExploreSearch): ExploreSearch["dateRange"] {
  const fromPreset = search.presetId
    ? presetIdToLegacyToken(search.presetId)
    : undefined;
  return (fromPreset as ExploreSearch["dateRange"] | undefined) ?? search.dateRange;
}

export function exploreSearchToMyBrandFilters(
  search: ExploreSearch,
  productFallback: Pick<MyBrandFilters, "products">,
  accountFallback: string[] | null = null,
  options: ExploreSearchPersistOptions = {},
): MyBrandFilters {
  let accounts: string[] | null;
  if (search.accounts === ALL_ACCOUNTS || search.accounts === undefined) {
    accounts = accountFallback;
  } else {
    const parsed = splitCsv(search.accounts);
    accounts = parsed.length > 0 ? parsed : [];
  }
  const platformsRaw = search.platforms;
  if (platformsRaw === undefined) {
    return {
      platforms: null,
      accounts,
      products: productFallback.products,
      dateRange: dateRangeFromSearch(search),
      ...dateSelectionFromSearch(search, options.persistCustomWindow === true),
    };
  }
  const platforms = splitCsv(platformsRaw);
  return {
    platforms: platforms.length > 0 ? platforms : [],
    accounts,
    products: productFallback.products,
    dateRange: dateRangeFromSearch(search),
    ...dateSelectionFromSearch(search, options.persistCustomWindow === true),
  };
}

export function appliedFiltersToExploreSearch(
  mode: DashboardMode,
  appliedCompetitor: CompetitorFilters,
  appliedMyBrand: MyBrandFilters,
  options: ExploreSearchPersistOptions = {},
): ExploreSearch {
  return appliedStateToExploreSearch(mode, appliedCompetitor, appliedMyBrand, options);
}

/**
 * The default preset is omitted from the URL, like `accounts: null` is.
 *
 * Emitting it would make a link without the param compare as out of sync with
 * default applied state, and the app would push a redundant param on load.
 */
function presetIdForUrl(
  activePresetId: ExploreDateSelectionFields["activePresetId"],
  persistCustomWindow: boolean,
): ExploreDateSelectionFields["activePresetId"] | undefined {
  if (activePresetId === DEFAULT_PRESET_ID) return undefined;
  if (activePresetId === CUSTOM_PRESET_ID) {
    return persistCustomWindow ? CUSTOM_PRESET_ID : undefined;
  }
  return activePresetId;
}

function customWindowForUrl(
  filters: ExploreDateSelectionFields,
  persistCustomWindow: boolean,
): Pick<ExploreSearch, "customStart" | "customEnd"> {
  if (
    persistCustomWindow &&
    filters.activePresetId === CUSTOM_PRESET_ID &&
    isValidWindow({ startDate: filters.startDate, endDate: filters.endDate })
  ) {
    return { customStart: filters.startDate, customEnd: filters.endDate };
  }
  return {};
}

/** Serialize applied filter state into URL search params for the active mode. */
export function appliedStateToExploreSearch(
  mode: DashboardMode,
  appliedCompetitor: CompetitorFilters,
  appliedMyBrand: MyBrandFilters,
  options: ExploreSearchPersistOptions = {},
): ExploreSearch {
  const persistCustomWindow = options.persistCustomWindow === true;
  if (mode === "competitors") {
    return {
      mode,
      dateRange: appliedCompetitor.dateRange as ExploreSearch["dateRange"],
      presetId: presetIdForUrl(appliedCompetitor.activePresetId, persistCustomWindow),
      platforms: joinCsv(appliedCompetitor.platforms),
      myIndustry: appliedCompetitor.myIndustry ?? undefined,
      myBrand: appliedCompetitor.myBrand ?? undefined,
      competitorsIndustry: appliedCompetitor.competitorsIndustry ?? undefined,
      mainCompetitor: appliedCompetitor.mainCompetitor ?? undefined,
      benchmark: joinCsv(appliedCompetitor.benchmark),
      ...customWindowForUrl(appliedCompetitor, persistCustomWindow),
    };
  }

  return {
    mode,
    dateRange: appliedMyBrand.dateRange as ExploreSearch["dateRange"],
    presetId: presetIdForUrl(appliedMyBrand.activePresetId, persistCustomWindow),
    platforms:
      appliedMyBrand.platforms === null ? undefined : joinCsv(appliedMyBrand.platforms),
    accounts:
      appliedMyBrand.accounts === null ? undefined : joinCsv(appliedMyBrand.accounts),
    ...customWindowForUrl(appliedMyBrand, persistCustomWindow),
  };
}

export function isExploreSearchInSyncWithApplied(
  search: ExploreSearch,
  mode: DashboardMode,
  appliedCompetitor: CompetitorFilters,
  appliedMyBrand: MyBrandFilters,
  options: ExploreSearchPersistOptions = {},
): boolean {
  const fromApplied = appliedStateToExploreSearch(
    mode,
    appliedCompetitor,
    appliedMyBrand,
    options,
  );
  return exploreSearchEquals(search, fromApplied);
}

export function hasExploreFilterParams(search: ExploreSearch): boolean {
  return Boolean(
    search.platforms ||
      search.myIndustry ||
      search.myBrand ||
      search.competitorsIndustry ||
      search.mainCompetitor ||
      search.benchmark ||
      search.accounts ||
      search.dateRange !== DEFAULT_EXPLORE_SEARCH.dateRange ||
      // Without this, `shouldHydrateStoreFromUrl` returns false and a bare
      // `?presetId=…` link is silently dropped on load.
      (search.presetId !== undefined && search.presetId !== DEFAULT_PRESET_ID) ||
      Boolean(search.customStart || search.customEnd),
  );
}

export function shouldHydrateStoreFromUrl(search: ExploreSearch): boolean {
  if (search.mode !== DEFAULT_EXPLORE_SEARCH.mode) return true;
  return hasExploreFilterParams(search);
}

export function shouldDeferFilterResetForUrlHydration(search: ExploreSearch): boolean {
  return shouldHydrateStoreFromUrl(search) && hasExploreFilterParams(search);
}

export function searchFromLocationHref(href: string): Record<string, unknown> {
  const idx = href.indexOf("?");
  if (idx < 0) return {};
  const raw = href.slice(idx + 1).split("#")[0] ?? "";
  return Object.fromEntries(new URLSearchParams(raw));
}

/** Preserve explore filter search when switching between dashboard / overview / drilldown. */
export function resolveExploreNavSearch(options: {
  targetPath: string;
  currentPath: string;
  href: string;
  lastRouteSearch: Record<string, string>;
}): Record<string, unknown> | undefined {
  const { targetPath, currentPath, href, lastRouteSearch } = options;
  if (EXPLORE_ROUTES.has(targetPath) && EXPLORE_ROUTES.has(currentPath)) {
    const current = searchFromLocationHref(href);
    return Object.keys(current).length > 0 ? current : undefined;
  }
  const stored = lastRouteSearch[targetPath];
  if (!stored) return undefined;
  return Object.fromEntries(new URLSearchParams(stored));
}
